import {
  MessageRatingFragment,
  MessageRatingReason,
  useRateMessageMutation,
  useRemoveMessageRatingMutation,
  useSaveMessageRatingDetailsMutation,
} from '@shared/generated/graphql';
import {
  removeMessageRatingFromCache,
  writeMessageRatingToCache,
} from '@shared/graphql/cache/messageRatingCache';
import { useCallback, useMemo, useRef } from 'react';

export type MessageRatingDetails = {
  reason: MessageRatingReason | null;
  comments: string;
};

export function useMessageFeedback(messageId: string) {
  const [rateMessage] = useRateMessageMutation();
  const [removeMessageRating] = useRemoveMessageRatingMutation();
  const [saveMessageRatingDetails] = useSaveMessageRatingDetailsMutation();
  const lastThumbMutationRef = useRef<Promise<unknown>>(Promise.resolve());

  // NOTE(aman): The API applies thumb clicks by lookup, so a removal that
  // overtakes the save it undoes finds nothing and the save wins.
  const runAfterLastThumbMutation = useCallback(
    <T>(mutate: () => Promise<T>): Promise<T> => {
      const result = lastThumbMutationRef.current
        .catch(() => undefined)
        .then(mutate);
      lastThumbMutationRef.current = result;
      return result;
    },
    []
  );

  const rate = useCallback(
    async (isPositive: boolean, current?: MessageRatingFragment) => {
      await runAfterLastThumbMutation(() =>
        rateMessage({
          variables: { input: { messageId, isPositive } },
          optimisticResponse: {
            rateMessage: {
              __typename: 'MessageRatingPayload',
              rating: {
                __typename: 'MessageRating',
                id: current?.id ?? `optimistic-${messageId}`,
                messageId,
                isPositive,
                reason: null,
                comments: null,
              },
            },
          },
          update: (cache, { data }) => {
            if (data) writeMessageRatingToCache(cache, data.rateMessage.rating);
          },
        })
      );
    },
    [messageId, rateMessage, runAfterLastThumbMutation]
  );

  const remove = useCallback(async () => {
    await runAfterLastThumbMutation(() =>
      removeMessageRating({
        variables: { input: { messageId } },
        optimisticResponse: {
          removeMessageRating: {
            __typename: 'RemoveMessageRatingPayload',
            success: true,
          },
        },
        update: (cache) => removeMessageRatingFromCache(cache, messageId),
      })
    );
  }, [messageId, removeMessageRating, runAfterLastThumbMutation]);

  const saveDetails = useCallback(
    async ({ reason, comments }: MessageRatingDetails) => {
      await saveMessageRatingDetails({
        variables: { input: { messageId, reason, comments } },
      });
    },
    [messageId, saveMessageRatingDetails]
  );

  return useMemo(
    () => ({ rate, remove, saveDetails }),
    [rate, remove, saveDetails]
  );
}
